import { Modal, ModalProps } from '@odin-labs/components';
import { TempBadgeStatus } from 'apollo/generated/client-operations';
import { CheckIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { useJobsiteTempBadgeTemplateOptions } from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/components/createTempBadgeModalContent/CreateTempBadgeModalContent.forms';
import { useJobsitePrinterOptions } from 'containers/utils';
import { ReactivateBadgeModalContent } from 'containers/worker/modals/badgeModal/components';
import { ReassignBadgeModalContent } from 'containers/worker/modals/badgeModal/components/ReassignBadgeModalContent';
import React from 'react';
import {
  CreateTempBadgeModalContent,
  PrintingTempBadgeModalContent,
  ReactivateTempBadgeModalContent,
  RegisterTempBadgeModalContent,
  useTempBadgeModal,
} from './components';
import { AssignTempBadgeModalContent } from './components/assignTempBadgeModalContent';
import {
  ReactivateBadgeState,
  TempBadgeModalProps,
  TempBadgingStep,
} from './types';

export function TempBadgeModal(props: TempBadgeModalProps): React.ReactElement {
  const { jobsite, navigateToTempBadgesOrRefresh } = props;
  const { jobsiteId } = jobsite ?? {};

  const {
    isOpen,
    closeModal,
    updateModalState,
    resetModalState,
    tempBadge,
    badgingStep,
    prevBadgingStep,
    reassignBadgeState,
    badgesToDeactivate,
    workerCardIdToReactivate,
    expirationDate,
    lastUsedPrinterOption,
  } = useTempBadgeModal();

  const reactivateBadge = (
    reactivateBadgeState: ReactivateBadgeState,
  ): void => {
    updateModalState({
      badgingStep: TempBadgingStep.ReactivateWorkerBadge,
      ...reactivateBadgeState,
    });
  };

  const workerCardsToDeactivate = React.useMemo(
    () => badgesToDeactivate?.map((b) => b.workerCard),
    [badgesToDeactivate],
  );

  const {
    jobsitePrinterOptions,
    defaultPrinterOption,
    loading: printersLoading,
  } = useJobsitePrinterOptions(jobsiteId);

  const {
    tempBadgeTemplateOptions,
    defaultTempBadgeTemplateOption,
    loading: templatesLoading,
  } = useJobsiteTempBadgeTemplateOptions(jobsiteId);

  const getModalProps = (): Omit<ModalProps, 'open' | 'setOpen'> => {
    if (printersLoading || templatesLoading) {
      return {
        actionsPanel: null,
        title: 'Loading printers & templates...',
        children: <LoadingError loading />,
      };
    }
    if (
      jobsitePrinterOptions.length === 0 ||
      tempBadgeTemplateOptions.length === 0
    ) {
      return {
        actionsPanel: null,
        title: 'Set up temporary badges',
        children: (
          <div>
            <p>
              This jobsite needs a printer and badge template configured before
              you can print temporary badges. Contact Odin support to get this
              set up, and we&apos;ll have you printing in no time.
            </p>
            <img
              src="https://storage.googleapis.com/odin-static-assets/odin3.0/badges/temp-badge-stack.svg"
              className="odin-h-100"
              alt="Illustration of a stack of temporary badges"
            />
          </div>
        ),
      };
    }
    switch (badgingStep) {
      case TempBadgingStep.CreateAndPrint:
        return {
          actionsPanel: null,
          title: 'Print a temporary badge',
          children: (
            <CreateTempBadgeModalContent
              closeModal={closeModal}
              onAction={(newTempBadge, selectedJobsitePrinter): void => {
                updateModalState({
                  badgingStep: TempBadgingStep.Printing,
                  tempBadge: newTempBadge,
                  lastUsedPrinterOption: selectedJobsitePrinter,
                });
                navigateToTempBadgesOrRefresh();
              }}
              jobsiteId={jobsiteId}
              jobsiteName={jobsite.name}
              jobsitePrinterOptions={jobsitePrinterOptions}
              defaultJobsitePrinterOption={defaultPrinterOption}
              tempBadgeTemplateOptions={tempBadgeTemplateOptions}
              defaultTempBadgeTemplateOption={defaultTempBadgeTemplateOption}
            />
          ),
        };
      case TempBadgingStep.Reprint:
      case TempBadgingStep.Printing:
      case TempBadgingStep.PrintFailed:
        return {
          actionsPanel: null,
          title:
            badgingStep === TempBadgingStep.PrintFailed
              ? 'Your badge failed to print'
              : 'Badge printing...',
          children: (
            <PrintingTempBadgeModalContent
              closeModal={closeModal}
              jobsiteName={jobsite.name}
              onAction={(printingTempBadge): void => {
                // update the tempBadge in the modal state
                // and transition to the next step if needed
                switch (printingTempBadge.status) {
                  case TempBadgeStatus.Printing:
                    updateModalState({
                      badgingStep: TempBadgingStep.Printing,
                      tempBadge: printingTempBadge,
                    });
                    // Perform refresh only when printing starts.
                    // For the other situations the refresh is done in
                    // JobsiteTempBadgesTab using polling.
                    navigateToTempBadgesOrRefresh();
                    break;
                  case TempBadgeStatus.Printed:
                    updateModalState({
                      badgingStep: TempBadgingStep.Register,
                      tempBadge: printingTempBadge,
                    });
                    break;
                  case TempBadgeStatus.PrintFailed:
                    updateModalState({
                      badgingStep: TempBadgingStep.PrintFailed,
                      tempBadge: printingTempBadge,
                    });
                    break;
                  default:
                    break;
                }
              }}
              lastUsedPrinterOption={lastUsedPrinterOption}
              jobsitePrinterOptions={jobsitePrinterOptions}
              defaultJobsitePrinterOption={defaultPrinterOption}
              tempBadge={tempBadge}
              badgingStep={badgingStep}
              prevBadgingStep={prevBadgingStep}
            />
          ),
        };
      case TempBadgingStep.Register:
        return {
          actionsPanel: null,
          title: 'Register a temporary badge',
          children: (
            <RegisterTempBadgeModalContent
              closeModal={closeModal}
              jobsiteName={jobsite.name}
              onAction={(registeredTempBadge): void => {
                if (!prevBadgingStep) {
                  closeModal();
                } else {
                  updateModalState({
                    badgingStep: TempBadgingStep.ConfirmFinished,
                    tempBadge: registeredTempBadge,
                  });
                }
                navigateToTempBadgesOrRefresh();
              }}
              jobsiteId={jobsiteId}
              tempBadge={tempBadge}
            />
          ),
        };
      case TempBadgingStep.ConfirmFinished:
        return {
          title: 'The badge has been printed',
          size: 'sm',
          onAction: (): void => {
            closeModal();
          },
          actionText: 'Close',
          actionIcon: CheckIcon,
        };
      case TempBadgingStep.Assign:
        return {
          actionsPanel: null,
          title: 'Assign temporary badge',
          children: (
            <AssignTempBadgeModalContent
              closeModal={closeModal}
              onAction={({
                tempBadge: assignedTempBadge,
                reassignState,
              }): void => {
                if (reassignState) {
                  updateModalState({
                    badgingStep: TempBadgingStep.ReassignWorkerBadge,
                    reassignBadgeState: reassignState,
                    tempBadge: assignedTempBadge,
                  });
                } else {
                  closeModal();
                  navigateToTempBadgesOrRefresh();
                }
              }}
              jobsiteId={jobsiteId}
              tempBadge={tempBadge}
              reactivateBadge={reactivateBadge}
              jobsiteName={jobsite.name}
            />
          ),
        };
      case TempBadgingStep.ReactivateWorkerBadge:
        return {
          actionsPanel: null,
          title: 'Reactivate badge',
          subtitle: 'Would you like to reactivate this badge?',
          children: (
            <ReactivateBadgeModalContent
              setOpen={closeModal}
              onAction={(reassignState): void => {
                if (reassignState) {
                  updateModalState({
                    badgingStep: TempBadgingStep.ReassignWorkerBadge,
                    reassignBadgeState: reassignState,
                  });
                } else {
                  closeModal();
                  navigateToTempBadgesOrRefresh();
                }
              }}
              workerCardIdToReactivate={workerCardIdToReactivate}
              expirationDate={expirationDate}
              workerCards={workerCardsToDeactivate}
            />
          ),
        };
      case TempBadgingStep.ReassignWorkerBadge:
        return {
          actionsPanel: null,
          title: `This badge is currently assigned to the following worker${
            reassignBadgeState?.workerCardInfo?.length > 1 ? 's' : ''
          }:`,
          children: (
            <ReassignBadgeModalContent
              setOpen={closeModal}
              onAction={(): void => {
                closeModal();
                navigateToTempBadgesOrRefresh();
              }}
              jobsiteId={jobsiteId}
              reassignBadgeState={reassignBadgeState}
            />
          ),
        };
      case TempBadgingStep.Reactivate:
        return {
          actionsPanel: null,
          title: 'Reactivate temporary badge',
          titleAlignment: 'center',
          size: 'xs',
          children: (
            <ReactivateTempBadgeModalContent
              closeModal={closeModal}
              jobsiteName={jobsite.name}
              onAction={(): void => {
                closeModal();
                navigateToTempBadgesOrRefresh();
              }}
              tempBadge={tempBadge}
            />
          ),
        };
      default:
        return {};
    }
  };

  const modalProps = getModalProps();

  return (
    <Modal
      open={isOpen}
      setOpen={closeModal}
      size="md"
      afterLeave={resetModalState}
      {...modalProps}
    />
  );
}
